import { useEffect, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { CheckCircle2, Copy, Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CheckoutFlow } from '@/components/subscription/checkout'
import { BILLING_GATE_HOME_PATH, alreadyHasLiveSubscription } from '@/lib/billing-gate'
import { getOrganizationBillingStateQueryOptions, useBillingState } from '@/stores/billing.store'
import { organizationStore } from '@/stores/organization.store'
import { BillingAccessStateEnum, BillingIntervalEnum } from '@/gen/cortado/v1/subscriptions_pb'

const SUCCESS_MOMENT_MS = 1500
// PAUSED_PAYMENT is a paid subscription that needs billing recovery rather
// than a second checkout. GRACE remains entitled and returns to the app.
const BILLING_MANAGEMENT_SEARCH = { section: 'subscription' as const }

export { alreadyHasLiveSubscription } from '@/lib/billing-gate'

export function resolveStartTrialRedirect(state: BillingAccessStateEnum | undefined) {
  if (alreadyHasLiveSubscription(state)) {
    return { to: BILLING_GATE_HOME_PATH } as const
  }
  if (
    state === BillingAccessStateEnum.BILLING_ACCESS_STATE_PAUSED_PAYMENT
  ) {
    return { to: '/company', search: BILLING_MANAGEMENT_SEARCH } as const
  }
  return null
}

export const Route = createFileRoute('/_app/start-trial')({
  beforeLoad: async ({ context }) => {
    const organizationId = organizationStore.state.selectedOrganizationId
    if (!organizationId) return

    const response = await context.queryClient
      .ensureQueryData(getOrganizationBillingStateQueryOptions(organizationId))
      .catch(() => null)
    const redirectTarget = resolveStartTrialRedirect(response?.billingState?.state)
    if (redirectTarget) {
      throw redirect(redirectTarget)
    }
  },
  component: StartTrialPage,
})

export function StartTrialPage() {
  const navigate = useNavigate()
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const organizationId = selectedOrganizationId ?? ''
  const { billingState, isLoading, hasLoaded } = useBillingState(organizationId)
  const [checkoutOutcome, setCheckoutOutcome] = useState<'trial' | 'subscription' | null>(null)
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)

  useEffect(() => {
    if (!billingState) return
    const redirectTarget = resolveStartTrialRedirect(billingState.state)
    if (!redirectTarget) return
    navigate({ ...redirectTarget, replace: true })
  }, [billingState, navigate])

  useEffect(() => {
    if (!checkoutOutcome) return
    const timeout = window.setTimeout(() => {
      navigate({ to: BILLING_GATE_HOME_PATH, replace: true })
    }, SUCCESS_MOMENT_MS)
    return () => window.clearTimeout(timeout)
  }, [checkoutOutcome, navigate])

  if (isLoading || !hasLoaded || !billingState || resolveStartTrialRedirect(billingState.state)) {
    return (
      <div className="flex min-h-full items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (checkoutOutcome) {
    return (
      <div className="flex min-h-full items-center justify-center bg-muted/30 p-6">
        <div role="status" className="w-full max-w-lg rounded-xl border bg-background p-10 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-12 w-12 text-status-success-fg" />
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            {checkoutOutcome === 'trial' ? 'Your trial is active' : 'Your subscription is active'}
          </h1>
          <p className="mt-2 text-muted-foreground">
            You have full access to Cortado Pro. Taking you back to your dashboard…
          </p>
          <Button className="mt-6" onClick={() => navigate({ to: BILLING_GATE_HOME_PATH, replace: true })}>
            Go to dashboard
          </Button>
        </div>
      </div>
    )
  }

  const copyOwnerLink = async () => {
    setCopyFailed(false)
    try {
      await navigator.clipboard.writeText(new URL('/start-trial', window.location.origin).toString())
      setCopied(true)
    } catch {
      setCopyFailed(true)
    }
  }

  return (
    <div className="flex min-h-full justify-center bg-muted/30 p-6 sm:p-10">
      <div className="w-full max-w-5xl self-start rounded-xl border bg-background p-6 shadow-sm sm:p-8">
        <div className="text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-status-info-bg text-status-info-fg">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-2xl font-bold tracking-tight">
            {billingState.trialAvailable ? 'Start your free trial' : 'Restart your subscription'}
          </h1>
        </div>

        <div className="mt-8">
          {billingState.canManageBilling ? (
            <CheckoutFlow
              organizationId={organizationId}
              billingState={billingState}
              defaultBillingInterval={BillingIntervalEnum.BILLING_INTERVAL_MONTHLY}
              onSuccess={() =>
                setCheckoutOutcome(billingState.trialAvailable ? 'trial' : 'subscription')
              }
            />
          ) : (
            <div role="status" className="rounded-lg border bg-muted/40 p-5 text-center">
              <h2 className="font-semibold">
                {billingState.trialAvailable
                  ? 'Ask your account owner to start the trial'
                  : 'Ask your account owner to restart the subscription'}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Only someone who can manage billing can add the payment method. Send them this link to pick up here.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => void copyOwnerLink()}>
                <Copy className="h-4 w-4" />
                {copied
                  ? 'Link copied'
                  : billingState.trialAvailable
                    ? 'Copy trial link'
                    : 'Copy subscription link'}
              </Button>
              {copyFailed && <p className="mt-2 text-sm text-destructive">Couldn&apos;t copy the link. Please copy it from your browser.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
